"use client";

// ===================================
// MODERNIZED CHAT PAGE WITH AI CONFIGURATION
// src/pages/Chat.tsx
// ENHANCED: Added multi-provider AI configuration while preserving all existing features
// 🔐 UPDATED: Added pendingAgenticAuth state tracking for inline authentication flow
// ===================================
import type React from "react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { useAIConfig } from "../contexts/AIConfigContext"; // NEW: AI Config context
import { chatApi, searchApi } from "../services/api";
import Header from "../components/Header";
import LoadingSpinner from "../components/LoadingSpinner";
import AgenticChecklist from "../components/AgenticChecklist";
import AgenticResponse from "../components/AgenticResponse";
import EnhancedMarkdown from "../components/EnhancedMarkdown";

import {
  Send,
  Search,
  Settings,
  Plus,
  MessageSquare,
  Archive,
  MoreVertical,
  Edit2,
  Trash2,
  Shield,
  Download,
  Sparkles,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Cpu,
  Zap,
  RefreshCw,
  Lock, // 🔐 NEW: Import Lock icon for auth indicator
} from "lucide-react";
import type { ChatSession, Message, SearchStats } from "../types";
import { notifyError, notifySuccess } from "../utils/Toastify";
import AiChatIcon from "../assets/aiChatIcon.png";

// ===================================
// NEW: AI Configuration Types
// ===================================
interface SessionAIConfig {
  mode?: string;
  llm_provider?: string;
  llm_model?: string;
}

interface AIMessageMetadata {
  mode?: string;
  mode_display?: string;
  llm_provider?: string;
  llm_model?: string;
  used_fallback?: boolean;
  processing_time?: number;
}

// 🔐 NEW: Pending Agentic Auth State Type
interface PendingAgenticAuth {
  type: 'setup' | 'verification' | null;
  intent?: any;
  attemptsRemaining?: number;
}

const Chat: React.FC = () => {
  const { user } = useAuth();
  const { hideChatSidebar } = useSettings();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  // ===================================
  // NEW: AI Configuration from Context
  // ===================================
  const { 
    effectiveConfig, 
    modes, 
    llmProviders, 
    isLoading: isAIConfigLoading,
    error: aiConfigError 
  } = useAIConfig();

  // ===================================
  // NEW: Session-level AI Configuration State
  // ===================================
  const [sessionAIConfig, setSessionAIConfig] = useState<SessionAIConfig>({});
  const [showAISettings, setShowAISettings] = useState(false);

  // 🔐 NEW: Pending Agentic Authentication State
  const [pendingAgenticAuth, setPendingAgenticAuth] = useState<PendingAgenticAuth>({ 
    type: null 
  });

  // Chat state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [showPlusMenu, setShowPlusMenu] = useState(false);

  // UI state
  const [showSessionMenu, setShowSessionMenu] = useState<{
    [sessionId: string]: boolean;
  }>({});
  const [editingSession, setEditingSession] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  // RAG settings (PRESERVED - now integrated with mode system)
  const [ragMode, setRagMode] = useState(true);
  const [ragSettings, setRagSettings] = useState({
    temperature: 0.1,
    include_citations: true,
    confidence_threshold: 0.7,
    max_tokens: 2000,
  });

  // Search stats
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null);

  const quickActions = [
    {
      icon: "📊",
      title: "Analyze Data",
      prompt: "Can you analyze my recent business data and provide insights?",
    },
    {
      icon: "📄",
      title: "Search Documents",
      prompt: "Help me find information in my documents about quarterly reports",
    },
    {
      icon: "✍️",
      title: "Create Report",
      prompt: "Create a comprehensive report summarizing this month's performance",
    },
    {
      icon: "💡",
      title: "Get Suggestions",
      prompt: "What are some recommendations to improve my business operations?",
    },
  ];

  const handleQuickAction = (prompt: string) => {
    setNewMessage(prompt);
    // Auto-focus input after setting message
    setTimeout(() => {
      const input = document.querySelector(
        'textarea[placeholder*="Message"]'
      ) as HTMLTextAreaElement;
      input?.focus();
    }, 100);
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const { hideChatMode, hideExport, hideSearch, debugMode } = useSettings();

  useEffect(() => {
    initializeChat();
    loadSearchStats();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        plusMenuRef.current &&
        !plusMenuRef.current.contains(event.target as Node)
      ) {
        setShowPlusMenu(false);
      }
    };

    if (showPlusMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPlusMenu]);

  // ===================================
  // NEW: Sync ragMode with AI config mode
  // ===================================
  useEffect(() => {
    const currentMode = sessionAIConfig.mode || effectiveConfig?.mode;
    if (currentMode) {
      // RAG modes include: RAG_ONLY, RAG_CHAT, RAG_AGENTIC, FULL
      const ragModes = ['RAG_ONLY', 'RAG_CHAT', 'RAG_AGENTIC', 'FULL'];
      setRagMode(ragModes.includes(currentMode));
    }
  }, [sessionAIConfig.mode, effectiveConfig?.mode]);

  const initializeChat = async () => {
    try {
      setIsLoadingSessions(true);
      await loadSessions();
    } catch (error) {
      notifyError("Failed to load chat sessions");
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await chatApi.getSessions({ limit: 50 });
      setSessions(response.data.sessions || response.data || []);

      if (
        !currentSession &&
        (response.data.sessions || response.data).length > 0
      ) {
        const mostRecent = (response.data.sessions || response.data)[0];
        setCurrentSession(mostRecent);
        await loadSessionMessages(mostRecent.id);
      }
    } catch (error) {}
  };

  const loadSessionMessages = async (sessionId: string) => {
    try {
      const response = await chatApi.getSession(sessionId, {
        message_limit: 50,
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      setMessages([]);
    }
  };

  const loadSearchStats = async () => {
    try {
      const response = await searchApi.stats();
      setSearchStats(response.data.search_analytics || response.data.data);
    } catch (error) {
      setSearchStats(null);
    }
  };

  const createNewSession = async (
    title?: string,
    type: "general" | "document_qa" | "rag_chat" = "rag_chat"
  ) => {
    try {
      const response = await chatApi.createSession({
        title: title || "New Conversation",
        type,
        context_documents: [],
      });

      const newSession = response.data.session;
      setSessions((prev) => [newSession, ...prev]);
      setCurrentSession(newSession);
      setMessages([]);

      // NEW: Reset session AI config for new session
      setSessionAIConfig({});
      
      // 🔐 NEW: Reset pending auth state for new session
      setPendingAgenticAuth({ type: null });

      notifySuccess("Created new conversation");

      return newSession;
    } catch (error) {
      notifyError("Failed to create new chat session");
    }
  };

  const switchSession = async (session: ChatSession) => {
    if (session.id === currentSession?.id) return;

    setCurrentSession(session);
    setIsLoading(true);
    
    // NEW: Reset session AI config when switching sessions
    setSessionAIConfig({});
    
    // 🔐 NEW: Reset pending auth state when switching sessions
    setPendingAgenticAuth({ type: null });
    
    try {
      await loadSessionMessages(session.id);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSessionTitle = async (sessionId: string, newTitle: string) => {
    try {
      await chatApi.updateSession(sessionId, { title: newTitle });
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, title: newTitle } : s))
      );

      if (currentSession?.id === sessionId) {
        setCurrentSession((prev) =>
          prev ? { ...prev, title: newTitle } : null
        );
      }

      setEditingSession(null);
      setEditTitle("");
    } catch (error) {
      notifyError("Failed to update session title");
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!window.confirm("Are you sure you want to delete this conversation?")) {
      return;
    }

    try {
      await chatApi.deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));

      if (currentSession?.id === sessionId) {
        const remainingSessions = sessions.filter((s) => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          switchSession(remainingSessions[0]);
        } else {
          createNewSession();
        }
      }
      notifySuccess("Conversation has been deleted");
    } catch (error) {
      notifyError("Failed to delete conversation");
    }
  };

  const archiveSession = async (sessionId: string) => {
    try {
      await chatApi.updateSession(sessionId, { status: "archived" });
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                status: "archived" as const,
              }
            : s
        )
      );
      notifySuccess("Conversation has been archived");
    } catch (error) {
      notifyError("Failed to archive conversation");
    }
  };

  // ===================================
  // NEW: AI Configuration Helpers
  // ===================================
  const getCurrentMode = () => {
    return sessionAIConfig.mode || effectiveConfig?.mode || 'RAG_CHAT';
  };

  const getCurrentModeInfo = () => {
    const currentMode = getCurrentMode();
    return modes?.find(m => m.key === currentMode) || {
      key: currentMode,
      displayName: currentMode.replace('_', ' '),
      icon: '🤖',
      description: ''
    };
  };

  const getCurrentProvider = () => {
    return sessionAIConfig.llm_provider || effectiveConfig?.llm_provider || 'claude-bedrock';
  };

  const getCurrentModel = () => {
    return sessionAIConfig.llm_model || effectiveConfig?.llm_model || '';
  };

  const hasAIOverrides = () => {
    return !!(sessionAIConfig.mode || sessionAIConfig.llm_provider || sessionAIConfig.llm_model);
  };

  const resetAIConfig = () => {
    setSessionAIConfig({});
    notifySuccess("AI settings reset to defaults");
  };

  const setMode = (mode: string) => {
    setSessionAIConfig(prev => ({ ...prev, mode }));
    
    // Update ragMode for backward compatibility
    const ragModes = ['RAG_ONLY', 'RAG_CHAT', 'RAG_AGENTIC', 'FULL'];
    setRagMode(ragModes.includes(mode));
  };

  const setModel = (provider: string, model: string) => {
    setSessionAIConfig(prev => ({ 
      ...prev, 
      llm_provider: provider, 
      llm_model: model 
    }));
  };

  // Build message config for API calls
  const getMessageConfig = () => {
    const config: Record<string, any> = {};
    
    if (sessionAIConfig.mode) {
      config.mode = sessionAIConfig.mode;
    }
    if (sessionAIConfig.llm_provider) {
      config.llm_provider = sessionAIConfig.llm_provider;
    }
    if (sessionAIConfig.llm_model) {
      config.llm_model = sessionAIConfig.llm_model;
    }
    
    return config;
  };

  // 🔐 NEW: Helper to get auth prompt hint
  const getAuthPromptHint = (): string | null => {
    if (pendingAgenticAuth.type === 'setup') {
      return "🔐 Set up your 6-digit security code to enable CRM actions";
    }
    if (pendingAgenticAuth.type === 'verification') {
      return `🔐 Enter your 6-digit code to continue${
        pendingAgenticAuth.attemptsRemaining 
          ? ` (${pendingAgenticAuth.attemptsRemaining} attempts remaining)` 
          : ''
      }`;
    }
    return null;
  };

  // ===================================
  // 🔐 HANDLE SEND MESSAGE WITH AI CONFIG + INLINE AUTH
  // ===================================
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading || !currentSession) return;

    const userMessageContent = newMessage.trim();
    setNewMessage("");
    setIsLoading(true);

    // 🔐 NEW: Capture current auth state BEFORE clearing (for the API call)
    const currentAuthState = { ...pendingAgenticAuth };
    
    // 🔐 NEW: Clear pending auth state optimistically (will be re-set if needed)
    // We don't clear it here - we'll clear it only on success or update it based on response

    // Add user message to UI immediately
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: userMessageContent,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // ===================================
      // ENHANCED: Include AI configuration + Auth context in message payload
      // ===================================
  const messageData = {
  message: userMessageContent,
  use_rag: ragMode && searchStats?.canPerformSearch,
  search_options: {
    limit: 5,
    similarity_threshold: ragSettings.confidence_threshold,
  },
  temperature: ragSettings.temperature,
  include_citations: ragSettings.include_citations,
  // AI configuration
  mode: sessionAIConfig.mode,
  llm_provider: sessionAIConfig.llm_provider,
  llm_model: sessionAIConfig.llm_model,
  // 🔐 Agentic auth context
  pendingAgenticSetup: currentAuthState.type === 'setup' ? true : undefined,
  pendingAgenticVerification: currentAuthState.type === 'verification' ? true : undefined,
};

      // 🔐 NEW: Add agentic auth context if pending
      // if (currentAuthState.type === 'setup') {
      //   messageData.pendingAgenticSetup = true;
      //   console.log('🔐 Sending with pendingAgenticSetup: true');
      // } else if (currentAuthState.type === 'verification') {
      //   messageData.pendingAgenticVerification = true;
      //   console.log('🔐 Sending with pendingAgenticVerification: true');
      // }

      if (currentAuthState.type) {
  console.log(`🔐 Sending with pending${currentAuthState.type === 'setup' ? 'AgenticSetup' : 'AgenticVerification'}: true`);
}

const response = await chatApi.sendMessage(
  currentSession.id,
  messageData
);

// 🔐 DEBUG: Log the entire response
console.log('📥 FULL API Response:', JSON.stringify(response.data, null, 2));
console.log('📥 Response keys:', Object.keys(response.data));

      // const response = await chatApi.sendMessage(
      //   currentSession.id,
      //   messageData
      // );

      // Handle both response formats: assistant_message (expected) or message (actual backend format)
      const responseMessage = response.data.assistant_message || response.data.message;
      
      if (!responseMessage) {
        throw new Error('No message in response');
      }

      // 🔐 NEW: Extract auth-related metadata from response
      const messageMetadata = responseMessage.metadata || {};
      const topLevelMetadata = response.data.metadata || {};
      
      // Check for auth requirements in both places
      const messageType = messageMetadata.type || topLevelMetadata.type;
      const needsAgenticSetup = messageMetadata.needsAgenticSetup || topLevelMetadata.needsAgenticSetup;
      const needsAgenticAuth = messageMetadata.needsAgenticAuth || topLevelMetadata.needsAgenticAuth;
      const agenticSetupComplete = messageMetadata.agenticSetupComplete || topLevelMetadata.agenticSetupComplete;
      const agenticAuthSuccess = messageMetadata.agenticAuthSuccess || topLevelMetadata.agenticAuthSuccess;
      const isLocked = messageMetadata.locked || topLevelMetadata.locked;
      const attemptsRemaining = messageMetadata.attemptsRemaining || topLevelMetadata.attemptsRemaining;

      // Build assistant message with proper structure
      const assistantMessage: Message = {
        id: responseMessage.id || Date.now().toString(),
        role: responseMessage.role || 'assistant',
        content: responseMessage.content,
        created_at: responseMessage.created_at || new Date().toISOString(),
        metadata: responseMessage.metadata || {},
      };
      
      // Merge AI metadata into message metadata
      if (response.data.metadata) {
        const enhancedMetadata = {
          ...assistantMessage.metadata,
          ai_config: {
            mode: response.data.metadata.mode,
            mode_display: response.data.metadata.mode_display,
            llm_provider: response.data.metadata.llm_provider,
            llm_model: response.data.metadata.llm_model,
            used_fallback: response.data.metadata.used_fallback,
            processing_time: response.data.metadata.processing_time,
          }
        } as Message['metadata'] & { ai_config?: AIMessageMetadata };
        
        assistantMessage.metadata = enhancedMetadata as Message['metadata'];
      }
      
      setMessages((prev) => [...prev, assistantMessage]);

      // 🔐 NEW: Update pending auth state based on response
      if (isLocked) {
        // Account is locked, clear pending state
        setPendingAgenticAuth({ type: null });
        console.log('🔐 Account locked, clearing auth state');
      } else if (agenticSetupComplete || agenticAuthSuccess) {
        // Auth completed successfully, clear pending state
        setPendingAgenticAuth({ type: null });
        console.log('🔐 Auth successful, clearing pending state');
      } else if (messageType === 'agentic_setup_required' || needsAgenticSetup) {
        // System is requesting setup
        setPendingAgenticAuth({ 
          type: 'setup', 
          intent: messageMetadata.intent || topLevelMetadata.intent 
        });
        console.log('🔐 Setup required, setting pendingAgenticAuth to setup');
      } else if (messageType === 'agentic_auth_required' || needsAgenticAuth) {
        // System is requesting verification
        setPendingAgenticAuth({ 
          type: 'verification', 
          intent: messageMetadata.intent || topLevelMetadata.intent,
          attemptsRemaining: attemptsRemaining
        });
        console.log('🔐 Verification required, setting pendingAgenticAuth to verification');
      } else {
        // Normal response, clear any pending auth state
        setPendingAgenticAuth({ type: null });
      }

      // Update session metadata
      setSessions((prev) =>
        prev.map((s) =>
          s.id === currentSession.id
            ? {
                ...s,
                message_count: s.message_count + 2,
                last_message_at: new Date().toISOString(),
              }
            : s
        )
      );
    } catch (error: any) {
      console.error("Error sending message:", error);

      // 🔐 NEW: Clear pending auth on error
      setPendingAgenticAuth({ type: null });

      // 🔐 Handle 403 errors (old agentic gate system - backward compatibility)
      if (error.response?.status === 403) {
        const errorData = error.response?.data;
        
        // Check if it's an agentic auth error
        if (
          errorData?.error === "AGENTIC_AUTH_REQUIRED" ||
          errorData?.error === "agentic_auth_required"
        ) {
          // Add auth notice message
          const authNotice: Message = {
            id: Date.now().toString() + "-auth",
            role: "assistant",
            content:
              "🔐 This action requires authentication. Please provide your access code to continue.",
            created_at: new Date().toISOString(),
            metadata: {
              type: "agentic_notice" as any,
            },
          };
          setMessages((prev) => [...prev, authNotice]);
          
          // 🔐 NEW: Set pending auth state for backward compatibility
          setPendingAgenticAuth({ type: 'verification' });
        } else {
          // Other 403 errors
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content:
              "You don't have permission to perform this action. Please contact your administrator.",
            created_at: new Date().toISOString(),
            metadata: { type: "error_response" },
          };
          setMessages((prev) => [...prev, errorMessage]);
        }
      } else {
        // Other errors
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content:
            "I encountered an issue processing your request. Please try again.",
          created_at: new Date().toISOString(),
          metadata: { type: "error_response" },
        };
        setMessages((prev) => [...prev, errorMessage]);

        notifyError("Failed to send message. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateMessage = async () => {
    if (!currentSession) return;

    try {
      setIsLoading(true);

      setMessages((prev) => {
        const lastIndex = prev.length - 1;
        if (lastIndex >= 0 && prev[lastIndex].role === "assistant") {
          return prev.slice(0, lastIndex);
        }
        return prev;
      });

      const response = await chatApi.regenerateMessage(currentSession.id, {
        use_rag: ragMode && searchStats?.canPerformSearch,
        temperature: ragSettings.temperature,
        // NEW: Include AI config in regenerate
        ...getMessageConfig(),
      });

      // Handle both response formats
      const responseMessage = response.data.assistant_message || response.data.message;
      
      if (responseMessage) {
        const newMessage: Message = {
          id: responseMessage.id || Date.now().toString(),
          role: responseMessage.role || 'assistant',
          content: responseMessage.content,
          created_at: responseMessage.created_at || new Date().toISOString(),
          metadata: responseMessage.metadata || {},
        };
        
        // Add AI metadata if present
        if (response.data.metadata) {
          newMessage.metadata = {
            ...newMessage.metadata,
            ai_config: response.data.metadata
          } as Message['metadata'];
        }
        
        setMessages((prev) => [...prev, newMessage]);
      }
    } catch (error) {
      notifyError("Failed to regenerate response");
    } finally {
      setIsLoading(false);
    }
  };

  const exportConversation = async () => {
    if (!currentSession) return;

    try {
      const conversationData = {
        session: currentSession,
        messages: messages,
        ai_config: sessionAIConfig, // NEW: Include AI config in export
        exported_at: new Date().toISOString(),
      };

      const dataStr = JSON.stringify(conversationData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `conversation-${currentSession.id}-${
        new Date().toISOString().split("T")[0]
      }.json`;
      link.click();

      URL.revokeObjectURL(url);
      notifySuccess("Conversation exported successfully");
    } catch (error) {
      notifyError("Failed to export conversation");
    }
  };

  // ===================================
  // NEW: AI Settings Panel Component (Inline)
  // ===================================
  const renderAISettingsPanel = () => {
    if (!showAISettings) return null;

    const currentModeInfo = getCurrentModeInfo();

    return (
      <div className="bg-[#0f172a]/90 border-b border-[#334155]/50 p-4 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          {/* Mode Selection */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-400 mb-2">
              Operation Mode
            </label>
            <div className="flex flex-wrap gap-2">
              {(modes || []).map((mode) => (
                <button
                  key={mode.key}
                  onClick={() => setMode(mode.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm ${
                    getCurrentMode() === mode.key
                      ? 'bg-[#8b3dff] border-[#8b3dff] text-white'
                      : 'bg-[#1e293b] border-[#334155] text-gray-400 hover:border-[#8b3dff] hover:text-white'
                  }`}
                  title={mode.description}
                >
                  <span>{mode.icon}</span>
                  <span>{mode.displayName}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Model Selection */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-400 mb-2">
              AI Model
            </label>
            <div className="flex flex-wrap gap-2">
              {(llmProviders || []).map((provider) => (
                <div key={provider.provider_key} className="space-y-1">
                  <div className="text-xs text-[#8b3dff] font-medium px-1">
                    {provider.display_name}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(provider.config?.models || []).map((model: string) => (
                      <button
                        key={model}
                        onClick={() => setModel(provider.provider_key, model)}
                        className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                          getCurrentProvider() === provider.provider_key && 
                          getCurrentModel() === model
                            ? 'bg-[#8b3dff] text-white'
                            : 'bg-[#1e293b] text-gray-400 hover:bg-[#334155] hover:text-white'
                        }`}
                      >
                        {model.split('/').pop() || model}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Config Info */}
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-[#334155]/50">
            <span>
              Using: {getCurrentProvider()} • {getCurrentMode()}
              {hasAIOverrides() && ' (custom)'}
            </span>
            {hasAIOverrides() && (
              <button
                onClick={resetAIConfig}
                className="flex items-center gap-1 text-[#8b3dff] hover:text-white transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Reset to defaults
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

const renderMessage = (message: Message) => {
  const isUser = message.role === "user";
  
  // Get type as string to avoid type narrowing issues
  const msgType = message.metadata?.type as string | undefined;
  
  const isRAGResponse = msgType === "rag_response";
  const isAgenticNotice = msgType === "agentic_notice";
  const isAgenticChecklist = msgType === "agentic_parameter_collection";
  const isAgenticReady = msgType === "agentic_ready" || msgType === "agentic_confirmation_required";
  const isAgenticResult = msgType === "agentic_action_result" || msgType === "agentic_result";
  const isError = msgType === "error_response";

  // Check for inline agentic auth messages
  const isAgenticSetup = msgType === "agentic_setup_required" || msgType === "agentic_setup";
  const isAgenticAuth = msgType === "agentic_auth_required" || msgType === "agentic_verification";
  const isAgenticLocked = msgType === "agentic_locked";

  // Get AI config metadata
  const messageMetadata = message.metadata as (Message['metadata'] & { ai_config?: AIMessageMetadata }) | undefined;
  const aiConfig = messageMetadata?.ai_config;

  // 🔐 Helper: Extract clean output message for agentic results
  const extractAgenticOutputMessage = (content: string): { 
    outputMessage: string; 
    caseNumber?: string;
    actionType?: string;
    technicalDetails: string;
  } => {
    // Extract outputMessage
    const outputMatch = content.match(/- outputMessage:\s*(.+?)(?:\n|$)/);
    // Extract case number
    const caseNumberMatch = content.match(/- caseNumber:\s*(\d+)/);
    // Extract action type from header
    const actionMatch = content.match(/✅ Action executed:\s*(.+?)(?:\n|$)/);
    
    if (outputMatch) {
      return {
        outputMessage: outputMatch[1].trim(),
        caseNumber: caseNumberMatch?.[1],
        actionType: actionMatch?.[1]?.trim(),
        technicalDetails: content
      };
    }
    
    // Fallback: return the whole content
    return {
      outputMessage: content,
      technicalDetails: content
    };
  };

  // ===== ERROR MESSAGES =====
  if (isError) {
    return (
      <div key={message.id} className="flex justify-center my-6">
        <div className="max-w-2xl px-5 py-4 rounded-xl bg-red-500/10 border border-red-500/30 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <span className="text-xl mt-0.5">⚠️</span>
            <div className="flex-1">
              <p className="text-sm text-red-300 leading-relaxed">
                {message.content}
              </p>
            </div>
          </div>
          <div className="mt-2 text-xs text-red-400/60 text-center">
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      </div>
    );
  }

  // ===== AGENTIC ACTION RESULT - CLEAN SUCCESS BOX =====
// ===== AGENTIC ACTION RESULT - CLEAN SUCCESS BOX =====
if (isAgenticResult) {
  // 🔐 Helper: Extract clean output message for agentic results (agnostic)
  const extractAgenticOutputMessage = (content: string): { 
    outputMessage: string; 
    technicalDetails: string;
  } => {
    // Try to extract outputMessage from the content
    const outputMatch = content.match(/- outputMessage:\s*(.+?)(?:\n|$)/);
    
    if (outputMatch) {
      return {
        outputMessage: outputMatch[1].trim(),
        technicalDetails: content
      };
    }
    
    // Fallback: return the whole content
    return {
      outputMessage: content,
      technicalDetails: content
    };
  };

  const { outputMessage, technicalDetails } = extractAgenticOutputMessage(message.content);
  
  return (
    <div key={message.id} className="flex items-start gap-4 mb-8">
      {/* Success Avatar */}
      <div className="flex-shrink-0">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-emerald-400/20">
          ✅
        </div>
      </div>

      {/* Success Message Box */}
      <div className="flex-1 max-w-2xl">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 shadow-xl backdrop-blur-sm">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 font-semibold text-sm">
              Action Completed Successfully
            </span>
          </div>
          
          {/* Main Output Message */}
          <p className="text-emerald-100 leading-relaxed text-sm">
            {outputMessage}
          </p>

          {/* Debug Mode: Show Technical Details */}
          {debugMode && (
            <details className="mt-4 pt-3 border-t border-emerald-500/20">
              <summary className="text-xs text-emerald-400/60 cursor-pointer hover:text-emerald-400 transition-colors select-none">
                🔧 Technical Details
              </summary>
              <pre className="mt-2 text-xs text-emerald-300/50 overflow-x-auto whitespace-pre-wrap bg-black/20 p-3 rounded-lg font-mono">
                {technicalDetails}
              </pre>
            </details>
          )}
        </div>

        {/* Timestamp & Metadata */}
        <div className="flex items-center gap-2 mt-2 ml-1 text-xs text-gray-600 flex-wrap">
          <span>
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          {aiConfig?.mode_display && (
            <>
              <span>•</span>
              <span>{aiConfig.mode_display}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

  // ===== REST OF EXISTING MESSAGE TYPES =====
  return (
    <div
      key={message.id}
      className={`flex items-start gap-4 mb-8 group ${
        isUser ? "flex-row-reverse" : ""
      }`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <div className="w-11 h-11 rounded-xl bg-[#1e1535] flex items-center justify-center text-white text-base font-semibold shadow-lg ring-2 ring-[#4a2d6b]/30">
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
        ) : isAgenticNotice || isAgenticSetup || isAgenticAuth ? (
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-amber-400/20">
            🔐
          </div>
        ) : isAgenticLocked ? (
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-red-400/20">
            🔒
          </div>
        ) : isAgenticChecklist ? (
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-yellow-400/20">
            📝
          </div>
        ) : isAgenticReady ? (
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center text-white text-xl shadow-lg ring-2 ring-emerald-400/20">
            ✅
          </div>
        ) : (
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] flex items-center justify-center shadow-lg ring-2 ring-[#8b3dff]/20 p-2">
            <img
              src={AiChatIcon}
              alt="AI"
              className="w-full h-full object-contain brightness-0 invert"
            />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div
        className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}
      >
        {isAgenticChecklist ? (
          <div className="max-w-3xl">
            <AgenticChecklist content={message.content} />
          </div>
        ) : (
          <div className="flex flex-col">
            <div
              className={`px-5 py-3.5 rounded-2xl shadow-sm backdrop-blur-sm
                ${
                  isUser
                    ? "bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] text-white shadow-[#8b3dff]/20"
                    : isRAGResponse
                    ? "bg-[#0f172a]/80 border border-[#334155]/50 text-gray-100 shadow-xl"
                    : isAgenticNotice || isAgenticSetup || isAgenticAuth
                    ? "bg-amber-500/10 border border-amber-500/30 text-amber-100"
                    : isAgenticLocked
                    ? "bg-red-500/10 border border-red-500/30 text-red-100"
                    : isAgenticReady
                    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-100"
                    : "bg-[#0f172a]/80 border border-[#334155]/50 text-gray-100 shadow-xl"
                }
                ${
                  message.content.length < 50
                    ? "max-w-sm"
                    : message.content.length < 150
                    ? "max-w-xl"
                    : message.content.length < 300
                    ? "max-w-2xl"
                    : "max-w-3xl"
                }
              `}
            >
              <EnhancedMarkdown content={message.content} />

              {/* Confidence Score for RAG responses */}
              {isRAGResponse &&
                message.metadata?.confidence_score !== undefined && (
                  <div className="mt-3 pt-3 border-t border-[#334155]/50 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#8b3dff]" />
                      <span className="text-xs font-medium text-[#8b3dff]">
                        Confidence:{" "}
                        {(message.metadata.confidence_score * 100).toFixed(0)}
                        %
                      </span>
                    </div>
                    {message.metadata.confidence_score < 0.7 && (
                      <span className="text-xs text-amber-400 flex items-center gap-1">
                        <span>⚠️</span>
                        Low confidence
                      </span>
                    )}
                  </div>
                )}
            </div>

            {/* Timestamp and AI Metadata */}
            <div
              className={`flex items-center gap-2 mt-2 ml-1 text-xs flex-wrap ${
                isUser ? "text-purple-400/60 justify-end" : "text-gray-600"
              }`}
            >
              <span>
                {new Date(message.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {isRAGResponse && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    RAG
                  </span>
                </>
              )}
              {/* AI Config Metadata Display */}
              {!isUser && aiConfig && (
                <>
                  {aiConfig.mode_display && (
                    <>
                      <span>•</span>
                      <span>{aiConfig.mode_display}</span>
                    </>
                  )}
                  {aiConfig.llm_provider && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Cpu className="w-3 h-3" />
                        {aiConfig.llm_provider}
                      </span>
                    </>
                  )}
                  {aiConfig.used_fallback && (
                    <span className="text-amber-400">(fallback)</span>
                  )}
                  {aiConfig.processing_time && (
                    <>
                      <span>•</span>
                      <span>{aiConfig.processing_time}ms</span>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Generation Metadata */}
            {!isUser && message.metadata?.generation_metadata && (
              <div className="mt-2 ml-1 flex items-center gap-2 text-xs text-gray-600">
                <span className="font-medium">
                  {message.metadata.generation_metadata.model}
                </span>
                <span>•</span>
                <span>
                  {message.metadata.generation_metadata.generation_time_ms}ms
                </span>
                {message.metadata.generation_metadata.context_chunks_used >
                  0 && (
                  <>
                    <span>•</span>
                    <span>
                      {
                        message.metadata.generation_metadata
                          .context_chunks_used
                      }{" "}
                      chunks
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

  return (
    <div
      className="h-screen flex flex-col overflow-hidden"
      style={{
        background:
          "linear-gradient(to bottom right, #0a0f1e 0%, #0f172a 50%, #1e1b4b 100%)",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Fixed Header */}
      <div className="flex-shrink-0">
        <Header
          title={currentSession?.title || "Slavo AI Assistant"}
          subtitle={
            searchStats?.canPerformSearch
              ? `Ready to help • ${searchStats.total_documents} documents indexed`
              : "Setting up knowledge base..."
          }
          showNavigation={!hideChatSidebar}
        />
      </div>

      {/* NEW: AI Config Status Bar (when overrides active) */}
      {hasAIOverrides() && (
        <div className="flex-shrink-0 bg-[#8b3dff]/10 border-b border-[#8b3dff]/30 px-4 py-2">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <Zap className="w-4 h-4 text-[#8b3dff]" />
              <span className="text-[#8b3dff]">Custom AI Settings:</span>
              <span className="text-white">
                {getCurrentModeInfo().icon} {getCurrentModeInfo().displayName}
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-300">{getCurrentProvider()}</span>
            </div>
            <button
              onClick={resetAIConfig}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Reset
            </button>
          </div>
        </div>
      )}

      {/* 🔐 NEW: Pending Auth Status Bar */}
      {pendingAgenticAuth.type && (
        <div className="flex-shrink-0 bg-amber-500/10 border-b border-amber-500/30 px-4 py-2">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm">
              <Lock className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300">
                {pendingAgenticAuth.type === 'setup' 
                  ? 'Security Setup Required' 
                  : 'Authentication Required'}
              </span>
              {pendingAgenticAuth.attemptsRemaining && (
                <span className="text-amber-400/70 text-xs">
                  ({pendingAgenticAuth.attemptsRemaining} attempts remaining)
                </span>
              )}
            </div>
            <button
              onClick={() => setPendingAgenticAuth({ type: null })}
              className="text-xs text-amber-400 hover:text-white flex items-center gap-1 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* NEW: AI Settings Panel (Collapsible) */}
      {renderAISettingsPanel()}

      {/* Main Content Area - with overflow-hidden */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar (PRESERVED) */}
        {!hideChatSidebar && (
          <div
            className={`w-80 bg-[#0a0f1e]/90 backdrop-blur-xl border-r border-[#334155]/30 flex flex-col shadow-2xl transition-all duration-300 ease-in-out ${
              isSidebarVisible 
                ? "relative translate-x-0" 
                : "absolute -translate-x-full"
            }`}
            style={{ zIndex: 10 }}
          >
            {/* Sidebar Header */}
            <div className="p-5 border-b border-[#334155]/30">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#8b3dff]" />
                  Conversations
                </h2>
                <button
                  onClick={() => createNewSession()}
                  className="p-2.5 text-white bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] hover:from-[#7a35e6] hover:to-[#5c28bf] rounded-xl transition-all shadow-lg shadow-[#8b3dff]/20 hover:shadow-[#8b3dff]/40"
                  title="New conversation"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Search stats */}
              {searchStats && (
                <div className="px-3 py-2 bg-[#1e293b]/50 rounded-lg border border-[#334155]/30">
                  <div className="flex items-center gap-2 text-xs">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        searchStats.canPerformSearch
                          ? "bg-emerald-500 shadow-lg shadow-emerald-500/50"
                          : "bg-amber-500 shadow-lg shadow-amber-500/50"
                      } animate-pulse`}
                    ></div>
                    <span className="text-gray-400">
                      {searchStats.canPerformSearch
                        ? `${searchStats.chunks_with_embeddings} chunks indexed`
                        : "Preparing knowledge base..."}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Sessions List (PRESERVED) */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {isLoadingSessions ? (
                <div className="p-6 text-center">
                  <LoadingSpinner
                    size="sm"
                    message="Loading conversations..."
                  />
                </div>
              ) : sessions.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#1e293b]/50 border border-[#334155]/30 flex items-center justify-center">
                    <MessageSquare className="w-8 h-8 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500">No conversations yet</p>
                </div>
              ) : (
                <div className="space-y-2 p-3">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`group relative p-4 rounded-xl cursor-pointer transition-all ${
                        currentSession?.id === session.id
                          ? "bg-gradient-to-br from-[#8b3dff]/20 to-[#6b2fd4]/10 border border-[#8b3dff]/30 shadow-lg shadow-[#8b3dff]/10"
                          : "hover:bg-[#1e293b]/50 border border-transparent"
                      }`}
                      onClick={() => switchSession(session)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        {editingSession === session.id ? (
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onBlur={() =>
                              updateSessionTitle(session.id, editTitle)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                updateSessionTitle(session.id, editTitle);
                              } else if (e.key === "Escape") {
                                setEditingSession(null);
                                setEditTitle("");
                              }
                            }}
                            className="text-sm font-medium text-white bg-transparent border-none outline-none w-full"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <h3 className="text-sm font-medium text-white truncate pr-2 flex items-center gap-2">
                            {currentSession?.id === session.id && (
                              <ChevronRight className="w-4 h-4 text-[#8b3dff]" />
                            )}
                            {session.title}
                          </h3>
                        )}

                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSessionMenu((prev) => ({
                                ...prev,
                                [session.id]: !prev[session.id],
                              }));
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-500 hover:text-white hover:bg-[#1e293b] rounded-lg transition-all"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {showSessionMenu[session.id] && (
                            <div className="absolute right-0 mt-1 w-48 bg-[#1e293b] rounded-xl shadow-2xl py-2 z-10 border border-[#334155]/50 backdrop-blur-xl">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingSession(session.id);
                                  setEditTitle(session.title);
                                  setShowSessionMenu((prev) => ({
                                    ...prev,
                                    [session.id]: false,
                                  }));
                                }}
                                className="flex items-center w-full px-4 py-2.5 text-sm text-white hover:bg-[#0f172a] transition-colors"
                              >
                                <Edit2 className="w-4 h-4 mr-3" />
                                Rename
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveSession(session.id);
                                  setShowSessionMenu((prev) => ({
                                    ...prev,
                                    [session.id]: false,
                                  }));
                                }}
                                className="flex items-center w-full px-4 py-2.5 text-sm text-white hover:bg-[#0f172a] transition-colors"
                              >
                                <Archive className="w-4 h-4 mr-3" />
                                Archive
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSession(session.id);
                                  setShowSessionMenu((prev) => ({
                                    ...prev,
                                    [session.id]: false,
                                  }));
                                }}
                                className="flex items-center w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                              >
                                <Trash2 className="w-4 h-4 mr-3" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{session.message_count} messages</span>
                        <span>
                          {new Date(
                            session.last_message_at || session.created_at
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      {session.metadata?.type && (
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                              session.metadata.type === "rag_chat"
                                ? "bg-[#8b3dff]/20 text-[#8b3dff] border border-[#8b3dff]/30"
                                : session.metadata.type === "document_qa"
                                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                : "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                            }`}
                          >
                            {session.metadata.type === "rag_chat"
                              ? "🧠"
                              : session.metadata.type === "document_qa"
                              ? "📄"
                              : "💬"}
                            {session.metadata.type.replace("_", " ")}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col w-full">
          {/* Messages Container - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar w-full">
            {!currentSession ? (
              // ===================================
              // 🎨 NO SESSION - CENTERED WELCOME (PRESERVED)
              // ===================================
              <div className="flex items-center justify-center h-full">
                <div className="text-center py-12 w-full max-w-2xl mx-auto px-4">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] flex items-center justify-center shadow-2xl shadow-[#8b3dff]/30">
                    <MessageSquare className="w-14 h-14 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    Welcome to Slavo AI
                  </h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto">
                    Create a new conversation to get started with your AI
                    assistant
                  </p>
                  <button
                    onClick={() => createNewSession()}
                    className="px-6 py-3 bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] text-white rounded-xl font-medium shadow-lg shadow-[#8b3dff]/30 hover:shadow-[#8b3dff]/50 transition-all"
                  >
                    Start New Conversation
                  </button>
                </div>
              </div>
            ) : messages.length === 0 ? (
              // ===================================
              // 🎨 EMPTY SESSION - CENTERED WELCOME + QUICK ACTIONS (PRESERVED)
              // ===================================
              <div className="flex items-center justify-center h-full">
                <div className="text-center py-12 w-full max-w-4xl mx-auto px-4">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] flex items-center justify-center shadow-2xl shadow-[#8b3dff]/30 p-4">
                    <img
                      src={AiChatIcon}
                      alt="AI"
                      className="w-full h-full object-contain brightness-0 invert"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Hi {user?.name}! 👋
                  </h3>
                  <p className="text-gray-400 mb-2 max-w-xl mx-auto">
                    I'm Slavo, your AI assistant for {user?.current_business}
                  </p>
                  <p className="text-sm text-gray-500 max-w-xl mx-auto mb-8">
                    I can help you find information, answer questions about your
                    documents, and assist with various tasks including dynamic
                    agentic actions.
                  </p>

                  {/* Quick Actions - Centered Grid (PRESERVED) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                    {quickActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickAction(action.prompt)}
                        className="group p-4 bg-[#1e293b] hover:bg-[#334155] border border-[#334155] hover:border-[#8b3dff]/50 rounded-xl transition-all text-left hover:shadow-lg hover:shadow-[#8b3dff]/20"
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl flex-shrink-0">
                            {action.icon}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-medium mb-1 group-hover:text-[#8b3dff] transition-colors">
                              {action.title}
                            </h4>
                            <p className="text-sm text-gray-400 line-clamp-2">
                              {action.prompt}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              // ===================================
              // MESSAGES EXIST - SHOW THEM (PRESERVED + ENHANCED)
              // ===================================
              <div className="w-full max-w-4xl mx-auto">
                {messages.map(renderMessage)}

                {/* Loading Indicator (PRESERVED) */}
                {isLoading && (
                  <div className="flex items-start gap-4 mb-8">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] flex items-center justify-center shadow-lg ring-2 ring-[#8b3dff]/20 p-2">
                      <img
                        src={AiChatIcon}
                        alt="AI"
                        className="w-full h-full object-contain brightness-0 invert"
                      />
                    </div>
                    <div className="max-w-md">
                      <div className="bg-[#0f172a]/80 border border-[#334155]/50 px-5 py-3.5 rounded-2xl shadow-xl backdrop-blur-sm">
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1.5">
                            <div className="w-2 h-2 bg-[#8b3dff] rounded-full animate-bounce"></div>
                            <div
                              className="w-2 h-2 bg-[#8b3dff] rounded-full animate-bounce"
                              style={{ animationDelay: "0.1s" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-[#8b3dff] rounded-full animate-bounce"
                              style={{ animationDelay: "0.2s" }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-400">
                            {/* 🔐 NEW: Show auth-specific loading text */}
                            {pendingAgenticAuth.type === 'setup'
                              ? "Setting up security..."
                              : pendingAgenticAuth.type === 'verification'
                              ? "Verifying code..."
                              : ragMode && searchStats?.canPerformSearch
                              ? "Searching documents..."
                              : "Thinking..."}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message Input - Fixed Footer (Full Width) - PRESERVED + ENHANCED */}
      <div className="flex-shrink-0 bg-[#0a0f1e]/50 backdrop-blur-xl border-t border-[#334155]/30 p-6">
        <div 
          className="w-full flex justify-center transition-all duration-300"
          style={{
            paddingLeft: !hideChatSidebar && isSidebarVisible ? '260px' : '0px',
          }}
        >
          <div className="w-full max-w-4xl flex items-center gap-4">
            {/* Sidebar Toggle Button - Left Side (PRESERVED) */}
            {!hideChatSidebar && (
              <button
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                className="flex-shrink-0 p-3 bg-gradient-to-br from-[#8b3dff] to-[#6b2fd4] hover:from-[#7a35e6] hover:to-[#5c28bf] text-white rounded-xl shadow-lg shadow-[#8b3dff]/30 hover:shadow-[#8b3dff]/50 transition-all group"
                title={
                  isSidebarVisible ? "Hide Chat History" : "Show Chat History"
                }
              >
                <div className="flex items-center gap-2">
                  {isSidebarVisible ? (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                        />
                      </svg>
                      <span className="text-sm font-medium hidden group-hover:inline-block whitespace-nowrap">
                        Hide History
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 5l7 7-7 7M5 5l7 7-7 7"
                        />
                      </svg>
                      <span className="text-sm font-medium hidden group-hover:inline-block whitespace-nowrap">
                        Show History
                      </span>
                    </>
                  )}
                </div>
              </button>
            )}

            {/* Message Input Form - Takes Remaining Space */}
            <div className="flex-1">
              <form
                onSubmit={handleSendMessage}
                className="relative flex items-center gap-2 bg-[#1e293b]/80 border border-[#334155]/50 rounded-2xl px-4 py-3 shadow-xl shadow-[#8b3dff]/5 focus-within:border-[#8b3dff]/50 focus-within:shadow-[#8b3dff]/20 focus-within:bg-[#1e293b] transition-all"
              >
                {/* Plus Button and Menu Container (PRESERVED + ENHANCED) */}
                <div ref={plusMenuRef} className="relative flex-shrink-0">
                  {/* Plus Button */}
                  <button
                    type="button"
                    onClick={() => setShowPlusMenu(!showPlusMenu)}
                    className="text-gray-500 hover:text-[#8b3dff] transition-colors p-2 rounded-lg hover:bg-[#334155]/50"
                  >
                    <Plus className="w-5 h-5" />
                  </button>

                  {/* Plus Menu - ENHANCED with mode system */}
                  {showPlusMenu && (
                    <div className="absolute bottom-full mb-2 left-0 bg-[#1e293b] border border-[#334155]/50 rounded-xl shadow-2xl py-2 min-w-64 z-10 backdrop-blur-xl">
                      {/* Mode Selection Section */}
                      <div className="px-4 py-2 border-b border-[#334155]/50">
                        <span className="text-xs font-medium text-gray-400">Mode</span>
                      </div>
                      
                      {/* Dynamic Modes from Context */}
                      {(modes || []).slice(0, 4).map((mode) => (
                        <button
                          key={mode.key}
                          onClick={() => {
                            setMode(mode.key);
                            setShowPlusMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-white hover:bg-[#0f172a] flex items-center gap-3 transition-colors"
                        >
                          <span className={getCurrentMode() === mode.key ? "text-[#8b3dff]" : "text-gray-500"}>
                            {getCurrentMode() === mode.key ? "✓" : mode.icon}
                          </span>
                          <span>{mode.displayName}</span>
                        </button>
                      ))}

                      {/* AI Settings Toggle */}
                      <button
                        onClick={() => {
                          setShowAISettings(!showAISettings);
                          setShowPlusMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-white hover:bg-[#0f172a] flex items-center gap-3 transition-colors"
                      >
                        <Cpu className="w-4 h-4 text-[#8b3dff]" />
                        <span>AI Settings</span>
                        {showAISettings ? (
                          <ChevronUp className="w-4 h-4 ml-auto text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 ml-auto text-gray-500" />
                        )}
                      </button>

                      <hr className="my-2 border-[#334155]/50" />

                      {/* Actions Section (PRESERVED) */}
                      <button
                        onClick={() => {
                          createNewSession();
                          setShowPlusMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-white hover:bg-[#0f172a] flex items-center gap-3 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>New Chat</span>
                      </button>

                      {!hideExport && (
                        <button
                          onClick={() => {
                            exportConversation();
                            setShowPlusMenu(false);
                          }}
                          disabled={!currentSession}
                          className="w-full px-4 py-2.5 text-left text-white hover:bg-[#0f172a] disabled:opacity-50 flex items-center gap-3 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          <span>Export Chat</span>
                        </button>
                      )}

                      {!hideSearch && (
                        <button
                          onClick={() => {
                            window.location.href = "/search";
                            setShowPlusMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-white hover:bg-[#0f172a] flex items-center gap-3 transition-colors"
                        >
                          <Search className="w-4 h-4" />
                          <span>Search</span>
                        </button>
                      )}

                      <button
                        onClick={() => {
                          window.location.href = "/settings";
                          setShowPlusMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-white hover:bg-[#0f172a] flex items-center gap-3 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* 🔐 NEW: Pending Auth Indicator OR Quick Mode Indicator */}
                {pendingAgenticAuth.type ? (
                  <div className="flex-shrink-0 px-2 py-1 bg-amber-500/20 rounded-lg border border-amber-500/30 animate-pulse">
                    <span className="text-xs text-amber-300 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      {pendingAgenticAuth.type === 'setup' ? 'Enter new code' : 'Enter code'}
                    </span>
                  </div>
                ) : hasAIOverrides() && (
                  <div className="flex-shrink-0 px-2 py-1 bg-[#8b3dff]/20 rounded-lg border border-[#8b3dff]/30">
                    <span className="text-xs text-[#8b3dff]">
                      {getCurrentModeInfo().icon} {getCurrentProvider().split('-')[0]}
                    </span>
                  </div>
                )}

                {/* Textarea (PRESERVED + ENHANCED) */}
                <textarea
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    const target = e.target;
                    target.style.height = "auto";
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                  placeholder={
                    // 🔐 NEW: Dynamic placeholder based on auth state
                    pendingAgenticAuth.type === 'setup'
                      ? "Enter a 6-digit security code to set up..."
                      : pendingAgenticAuth.type === 'verification'
                      ? "Enter your 6-digit code..."
                      : "Message Slavo AI..."
                  }
                  className="flex-1 bg-transparent text-white placeholder-gray-500 resize-none outline-none px-3 py-2 max-h-[200px] leading-relaxed overflow-y-auto custom-scrollbar"
                  disabled={isLoading || !currentSession}
                  rows={1}
                />

                {/* Send Button (PRESERVED) */}
                <button
                  type="submit"
                  disabled={
                    !newMessage.trim() || isLoading || !currentSession
                  }
                  className={`transition-all p-2 rounded-lg flex-shrink-0 ${
                    newMessage.trim()
                      ? "text-[#8b3dff] hover:text-white hover:bg-[#8b3dff]"
                      : "text-gray-500 hover:text-gray-400"
                  } disabled:opacity-40 disabled:hover:text-gray-500 disabled:hover:bg-transparent`}
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>

              {/* 🔐 NEW: Auth Hint Text */}
              {getAuthPromptHint() && (
                <div className="mt-2 text-center">
                  <p className="text-xs text-amber-400">
                    {getAuthPromptHint()}
                  </p>
                </div>
              )}

              {/* Debug Info (PRESERVED + ENHANCED) */}
              {debugMode && (
                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500">
                    {ragMode && searchStats?.canPerformSearch ? (
                      <>🧠 {getCurrentModeInfo().displayName} • {getCurrentProvider()} • Powered by your knowledge base</>
                    ) : ragMode ? (
                      <>⏳ {getCurrentModeInfo().displayName} • Processing your documents...</>
                    ) : (
                      <>💬 {getCurrentModeInfo().displayName} • {getCurrentProvider()}</>
                    )}
                  </p>
                  {currentSession && (
                    <p className="text-xs text-gray-600 mt-1">
                      Session: {currentSession.title} • {messages.length}{" "}
                      messages
                      {hasAIOverrides() && " • Custom AI settings"}
                      {/* 🔐 NEW: Show pending auth state in debug */}
                      {pendingAgenticAuth.type && ` • Auth: ${pendingAgenticAuth.type}`}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;